import React, { useState } from 'react';
import {
  Hotel,
  MapPin,
  Phone,
  Mail,
  Globe,
  Navigation,
  Info,
  ExternalLink,
  X,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { CongresoAlojamientoPublico } from '../types/congreso.types';
import { FormattedText } from './common/FormattedText';

export interface AccommodationsSectionProps {
  textoAlojamiento?: string | null;
  textoIntroductorio?: string | null;
  alojamientos?: CongresoAlojamientoPublico[];
  nombreCongreso?: string;
}

export const AccommodationsSection: React.FC<AccommodationsSectionProps> = ({
  textoAlojamiento,
  textoIntroductorio,
  alojamientos = [],
  nombreCongreso
}) => {
  const [selectedHotel, setSelectedHotel] = useState<CongresoAlojamientoPublico | null>(null);

  const textoFinal = (textoAlojamiento || textoIntroductorio)?.trim() || null;
  const tieneTexto = Boolean(textoFinal);
  const tieneAlojamientos = Boolean(alojamientos && alojamientos.length > 0);

  // Si no hay comunicado institucional ni hoteles activos, la sección no se renderiza
  if (!tieneTexto && !tieneAlojamientos) {
    return null;
  }

  const congresoNombre = nombreCongreso?.trim() || 'Congreso';

  return (
    <section id="alojamiento" className="py-24 bg-slate-50 relative border-t border-slate-200/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Encabezado de la Sección */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-sky-50 border border-brand-cyan/30 text-brand-navy text-xs font-bold uppercase tracking-wider mb-4 shadow-sm">
            <Hotel className="w-3.5 h-3.5 text-brand-cyan" />
            Hospedaje y Convenios
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-brand-navy tracking-tight">
            Alojamientos Sugeridos
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600">
            Establecimientos con tarifas especiales y condiciones preferenciales para los asistentes del evento.
          </p>
        </div>

        {/* Bloque Destacado: Comunicado y Advertencia Institucional (Estilo Ejecutivo y Armónico) */}
        {tieneTexto && (
          <div className="bg-gradient-to-br from-white via-sky-50/50 to-slate-50 border border-sky-200/80 rounded-3xl p-6 sm:p-8 mb-14 shadow-sm relative overflow-hidden backdrop-blur-sm">
            {/* Halo decorativo sutil en la esquina */}
            <div className="absolute -top-12 -right-12 w-40 h-40 bg-brand-cyan/10 rounded-full blur-2xl pointer-events-none"></div>

            <div className="flex items-start gap-4 sm:gap-5 relative z-10">
              <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-white border border-sky-200 shadow-sm flex items-center justify-center text-brand-cyan mt-1">
                <Info className="w-6 h-6" />
              </div>
              <div className="flex-1 space-y-2">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-navy/5 text-brand-navy text-[11px] font-bold tracking-wider uppercase">
                  <Sparkles className="w-3.5 h-3.5 text-brand-cyan" />
                  Información Institucional
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-brand-navy tracking-tight">
                  Información Importante sobre Reservas de Alojamiento
                </h3>
                <div className="text-slate-700 text-sm sm:text-base leading-relaxed">
                  <FormattedText text={textoFinal} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Grilla de Hoteles */}
        {tieneAlojamientos && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
            {alojamientos.map((hotel) => {
              const hotelId = hotel.idAlojamiento || hotel.idCongresoAlojamiento || 0;
              const webUrl = hotel.sitioWeb || hotel.sitioWebUrl;

              return (
                <div
                  key={hotelId}
                  onClick={() => setSelectedHotel(hotel)}
                  className="bg-white rounded-3xl border border-slate-200/90 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col group cursor-pointer"
                >
                  {/* Foto de Portada 16:9 con Fallback */}
                  <div className="aspect-video w-full relative bg-slate-100 overflow-hidden">
                    {hotel.imagenUrl ? (
                      <img
                        src={hotel.imagenUrl}
                        alt={hotel.nombre}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          const target = e.target as HTMLElement;
                          target.style.display = 'none';
                          const fallback = target.parentElement?.querySelector('.img-fallback');
                          if (fallback) fallback.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <div
                      className={`img-fallback w-full h-full bg-gradient-to-br from-brand-navy to-slate-800 flex flex-col items-center justify-center text-white p-4 text-center ${
                        hotel.imagenUrl ? 'hidden' : ''
                      }`}
                    >
                      <Hotel className="w-10 h-10 text-brand-cyan mb-2" />
                      <span className="text-xs font-bold tracking-wider uppercase text-slate-200">
                        {hotel.nombre}
                      </span>
                    </div>
                  </div>

                  {/* Cuerpo de la Tarjeta */}
                  <div className="p-6 sm:p-7 flex-1 flex flex-col space-y-4">
                    <div>
                      <h3 className="text-xl font-bold text-brand-navy group-hover:text-brand-cyan transition-colors tracking-tight leading-snug">
                        {hotel.nombre}
                      </h3>

                      {hotel.direccion && (
                        <p className="mt-1 text-xs text-slate-500 flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-brand-cyan flex-shrink-0" />
                          <span>{hotel.direccion}</span>
                        </p>
                      )}

                      {hotel.telefono && (
                        <p className="mt-1 text-xs text-slate-600 flex items-center gap-1.5">
                          <Phone className="w-3.5 h-3.5 text-brand-cyan flex-shrink-0" />
                          <span>Tel: {hotel.telefono}</span>
                        </p>
                      )}
                    </div>

                    {hotel.descripcion && (
                      <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed">
                        {hotel.descripcion}
                      </p>
                    )}

                    {/* Resumen de Servicios */}
                    {hotel.servicios && (
                      <div className="bg-sky-50/50 border border-sky-100 rounded-2xl p-3.5 text-xs text-slate-700">
                        <span className="font-bold text-brand-navy block mb-1 text-[11px] uppercase tracking-wider">
                          Servicios Incluidos
                        </span>
                        <div className="line-clamp-3">
                          <FormattedText text={hotel.servicios} />
                        </div>
                      </div>
                    )}

                    {/* Botón para abrir el Modal Detallado con Tarifas y Condiciones */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedHotel(hotel);
                      }}
                      className="w-full py-2.5 px-4 rounded-xl bg-sky-50 hover:bg-sky-100 text-brand-navy border border-sky-200 hover:border-brand-cyan/50 text-xs font-bold flex items-center justify-center gap-2 transition-all shadow-sm group-hover:bg-brand-navy group-hover:text-white group-hover:border-transparent mt-auto"
                    >
                      <span>Ver Tarifas y Detalles</span>
                      <ArrowRight className="w-3.5 h-3.5 text-brand-cyan group-hover:text-white transition-colors" />
                    </button>

                    {/* Contacto Directo y Enlaces */}
                    <div className="pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2">
                      {hotel.email && (
                        <a
                          href={`mailto:${hotel.email}?subject=Consulta Alojamiento - ${encodeURIComponent(congresoNombre)}`}
                          onClick={(e) => e.stopPropagation()}
                          className="py-1.5 px-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-[11px] font-semibold text-slate-700 inline-flex items-center gap-1.5 transition"
                          title={hotel.email}
                        >
                          <Mail className="w-3.5 h-3.5 text-slate-500" />
                          <span>Email</span>
                        </a>
                      )}

                      {hotel.googleMapsUrl && (
                        <a
                          href={hotel.googleMapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="py-1.5 px-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-[11px] font-semibold text-slate-700 inline-flex items-center gap-1 transition"
                          title="Ver en Google Maps"
                        >
                          <Navigation className="w-3.5 h-3.5 text-brand-cyan" />
                          <span>Mapa</span>
                        </a>
                      )}

                      {webUrl && (
                        <a
                          href={webUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="py-1.5 px-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-[11px] font-semibold text-slate-700 inline-flex items-center gap-1 transition ml-auto"
                          title="Abrir sitio web oficial"
                        >
                          <Globe className="w-3.5 h-3.5 text-slate-500" />
                          <ExternalLink className="w-3 h-3 text-slate-400" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Modal Popup Detallado de Alojamiento (Estilo Disertantes) */}
        {selectedHotel && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white rounded-3xl max-w-2xl w-full p-6 sm:p-8 relative shadow-2xl border border-slate-200 max-h-[90vh] flex flex-col overflow-hidden">
              {/* Botón Cerrar */}
              <button
                type="button"
                onClick={() => setSelectedHotel(null)}
                className="absolute top-5 right-5 z-20 p-2.5 rounded-full bg-slate-100 text-slate-500 hover:text-slate-900 hover:bg-slate-200 transition-colors shadow-sm"
                aria-label="Cerrar modal"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Contenido Scrolleable */}
              <div className="overflow-y-auto pr-1 space-y-6">
                {/* Foto en Gran Formato */}
                <div className="w-full h-52 sm:h-64 rounded-2xl overflow-hidden relative bg-slate-100 shadow-md flex-shrink-0">
                  {selectedHotel.imagenUrl ? (
                    <img
                      src={selectedHotel.imagenUrl}
                      alt={selectedHotel.nombre}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-brand-navy to-slate-800 flex flex-col items-center justify-center text-white p-4 text-center">
                      <Hotel className="w-12 h-12 text-brand-cyan mb-2" />
                      <span className="text-sm font-bold tracking-wider uppercase text-slate-200">
                        {selectedHotel.nombre}
                      </span>
                    </div>
                  )}
                </div>

                {/* Encabezado y Ubicación */}
                {/* Encabezado y Ubicación / Contacto */}
                <div>
                  <h3 className="text-2xl sm:text-3xl font-black text-brand-navy tracking-tight mb-2">
                    {selectedHotel.nombre}
                  </h3>

                  <div className="space-y-1.5 text-xs sm:text-sm text-slate-600">
                    {selectedHotel.direccion && (
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="flex items-center gap-1.5 font-medium">
                          <MapPin className="w-4 h-4 text-brand-cyan flex-shrink-0" />
                          {selectedHotel.direccion}
                        </span>

                        {selectedHotel.googleMapsUrl && (
                          <a
                            href={selectedHotel.googleMapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-brand-cyan font-bold hover:underline"
                          >
                            <Navigation className="w-3.5 h-3.5" />
                            <span>Ver en Google Maps</span>
                          </a>
                        )}
                      </div>
                    )}

                    {selectedHotel.telefono && (
                      <p className="flex items-center gap-1.5 font-medium text-slate-700">
                        <Phone className="w-4 h-4 text-brand-cyan flex-shrink-0" />
                        <span>Teléfono: {selectedHotel.telefono}</span>
                      </p>
                    )}
                  </div>
                </div>

                {/* Descripción General Completa */}
                {selectedHotel.descripcion && (
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {selectedHotel.descripcion}
                  </p>
                )}

                {/* Bloque: Tarifas de Convenio y Condiciones */}
                {(selectedHotel.tarifasCondiciones || selectedHotel.tarifas) && (
                  <div className="bg-slate-50 border border-slate-200/90 rounded-2xl p-5 text-xs sm:text-sm shadow-xs">
                    <span className="font-extrabold text-brand-navy block mb-2 text-xs uppercase tracking-wider">
                      Tarifas Especiales por Noche
                    </span>
                    <div className="text-slate-700 leading-relaxed">
                      <FormattedText text={selectedHotel.tarifasCondiciones || selectedHotel.tarifas} />
                    </div>
                  </div>
                )}

                {/* Bloque: Servicios Incluidos */}
                {selectedHotel.servicios && (
                  <div className="bg-sky-50/50 border border-sky-100 rounded-2xl p-5 text-xs sm:text-sm shadow-xs">
                    <span className="font-extrabold text-brand-navy block mb-2 text-xs uppercase tracking-wider">
                      Servicios Incluidos
                    </span>
                    <div className="text-slate-700 leading-relaxed">
                      <FormattedText text={selectedHotel.servicios} />
                    </div>
                  </div>
                )}

                {/* Acciones de Contacto y Reserva al Pie */}
                {((selectedHotel.email) || (selectedHotel.sitioWeb || selectedHotel.sitioWebUrl)) && (
                  <div className="pt-4 border-t border-slate-200 flex flex-wrap items-center gap-3">
                    {selectedHotel.email && (
                      <a
                        href={`mailto:${selectedHotel.email}?subject=Consulta Alojamiento - ${encodeURIComponent(congresoNombre)}`}
                        className="flex-1 min-w-[200px] py-3 px-5 rounded-xl bg-brand-navy hover:bg-slate-900 text-white text-xs sm:text-sm font-bold flex items-center justify-center gap-2 transition shadow-sm hover:shadow"
                        title={selectedHotel.email}
                      >
                        <Mail className="w-4 h-4 text-brand-cyan" />
                        <span>Enviar Email ({selectedHotel.email})</span>
                      </a>
                    )}

                    {(selectedHotel.sitioWeb || selectedHotel.sitioWebUrl) && (
                      <a
                        href={(selectedHotel.sitioWeb || selectedHotel.sitioWebUrl)!}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="py-3 px-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs sm:text-sm font-semibold inline-flex items-center gap-2 transition shadow-xs"
                        title="Abrir sitio web oficial"
                      >
                        <Globe className="w-4 h-4 text-slate-500" />
                        <span>Sitio Web</span>
                        <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default AccommodationsSection;
